// intentLabel — pure mapping from the cascade work-category intent enum to the
// operator-facing FRIENDLY label shown on the session top bar (Row 2).
//
// model-meta-hardening Phase 5. The raw enum comes from
// `PromptIntent['type']` (backend/src/voice/prompt-classifier.service.ts):
//   dev_work | deep_work (legacy alias for dev_work) | deep_reasoning
//   | cheap_task | session_mgmt
// plus a few ad-hoc values the gateway persists directly (`share`).
//
// OPERATOR DECISION (friendly form): "Building" (dev_work) · "Quick"
// (cheap_task) · "Deep reasoning" (deep_reasoning). Everything else falls back
// to a humanized snake_case rendering so the bar NEVER shows a raw token like
// `session_mgmt` and NEVER crashes on an unknown value.

/** Friendly labels for the three operator-named work categories. */
const FRIENDLY: Record<string, string> = {
  dev_work: 'Building',
  deep_work: 'Building', // legacy alias for dev_work — same friendly label
  cheap_task: 'Quick',
  deep_reasoning: 'Deep reasoning',
};

/**
 * Humanize a snake_case (or hyphen-case) token → Title-case words.
 * `session_mgmt` → `Session mgmt`, `share` → `Share`. Pure; never throws.
 */
function humanize(raw: string): string {
  const words = raw.replace(/[_-]+/g, ' ').trim();
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Map a raw intent enum value to its friendly top-bar label.
 *
 * Returns `null` for nullish/empty input (caller hides the chip). For any
 * unmapped non-empty value, returns a humanized fallback — never the raw token,
 * never a throw.
 */
export function intentFriendlyLabel(intentType: string | null | undefined): string | null {
  if (!intentType) return null;
  return FRIENDLY[intentType] ?? humanize(intentType);
}
